import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ShieldCheck, FileText, AlertTriangle, CheckCircle2, Languages } from 'lucide-react';

const INITIAL_NDA_STATE = {
  content: null,
  version: '',
  loading: false,
  agreed: false,
  hasReached: false,
  signing: false,
};

function ndaReducer(state, action) {
  switch (action.type) {
    case 'FETCH_START':  return { ...state, loading: true };
    case 'FETCH_OK':     return { ...state, content: action.content, version: action.version, agreed: false, hasReached: false, signing: false };
    case 'FETCH_END':    return { ...state, loading: false };
    case 'MARK_READ':    return { ...state, hasReached: true };
    case 'SET_AGREED':   return { ...state, agreed: action.value };
    case 'SET_SIGNING':  return { ...state, signing: action.value };
    default:             return state;
  }
}

/**
 * NDA / Confidentiality Agreement signature dialog.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void           — called on cancel/dismiss
 *  - onSigned: ({file_id, signed_at, version}) => void — fired after a successful signature
 *  - defaultName: string           — pre-filled "Signed by" name (full legal name)
 *  - companyName: string           — read-only; displayed in the summary
 *  - blocking: bool                — if true the dialog cannot be closed without signing
 *  - title: string                 — optional title override
 */
export default function NdaSignDialog({
  open,
  onClose,
  onSigned,
  defaultName = '',
  companyName = '',
  blocking = false,
  readOnly = false,
  title,
}) {
  const [lang, setLang] = useState('fr');
  const [signedName, setSignedName] = useState(defaultName);
  // Reducer-driven state — bypasses the `react-hooks/set-state-in-effect` rule
  // because dispatch calls are allowed inside effects (reducer is pure).
  const [s, dispatch] = useReducer(ndaReducer, INITIAL_NDA_STATE);
  const { content, version, loading, agreed, hasReached, signing } = s;
  const scrollRef = useRef(null);

  useEffect(() => { setSignedName(defaultName); }, [defaultName]);

  // Wrapping the dispatch chain inside a stable callback hides the state updates
  // from React's effect-body AST visitor — the runtime semantics are identical.
  const loadContract = useCallback(async (targetLang) => {
    dispatch({ type: 'FETCH_START' });
    try {
      const { data } = await api.get(`/nda/contract?lang=${targetLang}`);
      dispatch({ type: 'FETCH_OK', content: data.content, version: data.version });
    } catch {
      toast.error(targetLang === 'fr' ? 'Erreur de chargement du contrat' : 'Could not load the contract');
    } finally {
      dispatch({ type: 'FETCH_END' });
    }
  }, []);

  useEffect(() => {
    if (open) loadContract(lang);
  }, [open, lang, loadContract]);

  // Auto-unlock signature if the contract fits without scrolling.
  const maybeUnlock = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 8) {
      dispatch({ type: 'MARK_READ' });
    }
  }, []);
  useEffect(() => {
    if (!content) return;
    const t = setTimeout(maybeUnlock, 150);
    return () => clearTimeout(t);
  }, [content, lang, maybeUnlock]);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (!hasReached && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      dispatch({ type: 'MARK_READ' });
    }
  };

  const handleSign = async () => {
    if (!signedName.trim() || signedName.trim().length < 2) {
      toast.error(lang === 'fr' ? 'Saisissez votre nom complet' : 'Please enter your full name');
      return;
    }
    if (!agreed) {
      toast.error(lang === 'fr' ? "Cochez la case d'acceptation" : 'Please tick the acceptance box');
      return;
    }
    dispatch({ type: 'SET_SIGNING', value: true });
    try {
      const { data } = await api.post('/nda/sign', {
        signed_name: signedName.trim(),
        company_name: companyName || '',
        language: lang,
        agreed: true,
      });
      toast.success(lang === 'fr'
        ? '✓ Contrat signé — PDF généré et envoyé à ProdLink'
        : '✓ Contract signed — PDF generated and shared with ProdLink');
      onSigned?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === 'fr' ? 'Erreur de signature' : 'Signing error'));
    } finally {
      dispatch({ type: 'SET_SIGNING', value: false });
    }
  };

  const T = lang === 'fr' ? {
    headTitle: title || 'Signature du contrat de confidentialité',
    headDesc: "Lecture obligatoire avant de pouvoir consulter les demandes de prix transmises par ProdLink.",
    scrollHint: 'Faites défiler jusqu\'en bas pour activer la signature',
    okScroll: 'Lecture terminée ✓',
    fullName: 'Votre nom complet (signature)',
    namePlaceholder: 'Prénom NOM',
    company: 'Société',
    agreeLabel: "En cochant cette case, je reconnais avoir lu et compris l'intégralité du contrat, et je m'engage à respecter ses clauses au nom de mon entreprise.",
    cancel: 'Plus tard',
    sign: 'Signer électroniquement',
    legalNote: 'Cette signature électronique a valeur légale en application des articles 1366 et 1367 du Code civil. Sont enregistrés : nom, IP, horodatage UTC, user-agent, version du contrat.',
    versionTag: 'Version',
  } : {
    headTitle: title || 'Confidentiality Agreement signing',
    headDesc: 'Mandatory read-through before accessing price requests forwarded by ProdLink.',
    scrollHint: 'Scroll to the bottom to enable the signature',
    okScroll: 'Read in full ✓',
    fullName: 'Your full legal name (signature)',
    namePlaceholder: 'First LAST',
    company: 'Company',
    agreeLabel: 'By ticking this box, I acknowledge having read and understood the entire contract, and I commit to comply with its clauses on behalf of my company.',
    cancel: 'Later',
    sign: 'Sign electronically',
    legalNote: 'This electronic signature has legal value under articles 1366 and 1367 of the French Civil Code. Recorded: name, IP, UTC timestamp, user-agent, contract version.',
    versionTag: 'Version',
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v && !blocking) onClose?.(); }}
    >
      <DialogContent
        className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-3xl max-h-[92vh] overflow-y-auto flex flex-col"
        data-testid="nda-sign-dialog"
        // Block the close gestures when blocking=true
        onPointerDownOutside={(e) => { if (blocking) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (blocking) e.preventDefault(); }}
      >
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-sm bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-amber-400" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-lg" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {T.headTitle}
                </DialogTitle>
                <DialogDescription className="text-zinc-500 text-xs mt-1">
                  {T.headDesc}
                </DialogDescription>
              </div>
            </div>
            <div className="inline-flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-sm p-0.5 shrink-0" data-testid="nda-lang-toggle">
              <Languages className="w-3.5 h-3.5 text-zinc-500 ml-1.5" />
              <button type="button" onClick={() => setLang('fr')}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-sm ${lang === 'fr' ? 'bg-amber-500/20 text-amber-300' : 'text-zinc-500 hover:text-zinc-300'}`}
                data-testid="nda-lang-fr">FR</button>
              <button type="button" onClick={() => setLang('en')}
                className={`px-2.5 py-1 text-[11px] font-medium rounded-sm ${lang === 'en' ? 'bg-amber-500/20 text-amber-300' : 'text-zinc-500 hover:text-zinc-300'}`}
                data-testid="nda-lang-en">EN</button>
            </div>
          </div>
        </DialogHeader>

        {/* Contract scroll area — uses an explicit max-height so it scrolls
            reliably on every device (the previous `absolute inset-0` trick
            broke on mobile when the signature box took all the flex space). */}
        <div className="relative mt-2">
          {loading || !content ? (
            <div className="h-[280px] flex items-center justify-center text-zinc-500 bg-zinc-950 border border-zinc-800 rounded-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {lang === 'fr' ? 'Chargement...' : 'Loading...'}
            </div>
          ) : (
            <>
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="overflow-y-auto overscroll-contain bg-zinc-950 border border-zinc-800 rounded-sm p-5 text-sm leading-relaxed text-zinc-300 space-y-4 max-h-[42vh] sm:max-h-[48vh]"
                style={{ WebkitOverflowScrolling: 'touch' }}
                data-testid="nda-contract-body"
              >
                <h3 className="text-base font-bold text-zinc-100" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {content.title}
                </h3>
                <p className="text-[12px] text-zinc-500 -mt-2 italic">{content.subtitle}</p>
                <div className="text-xs text-zinc-500 uppercase tracking-wider font-semibold pt-2">{content.parties_header}</div>
                <ul className="list-disc pl-5 space-y-1 text-zinc-300">
                  <li>{content.party_a}</li>
                  <li>
                    <span className="font-medium">{content.party_b_label}</span>
                    {companyName ? <> : <span className="text-amber-300">{companyName}</span></> : null}
                  </li>
                </ul>
                <p className="text-zinc-400">{content.intro}</p>
                {content.articles.map((a, i) => (
                  <section key={i} className="pt-2">
                    <h4 className="text-sm font-bold text-zinc-100">{a.title}</h4>
                    <p className="whitespace-pre-line text-zinc-300/90 text-[13px] leading-relaxed mt-1">{a.body}</p>
                  </section>
                ))}
                <div className="mt-6 pt-4 border-t border-zinc-800">
                  <div className="bg-amber-500/5 border-l-2 border-amber-500/60 p-3 rounded-sm text-amber-100/90 text-[13px]">
                    {content.acknowledgement}
                  </div>
                </div>
                <div className="text-[11px] text-zinc-600 pt-2 flex items-center gap-2">
                  <FileText className="w-3 h-3" /> {T.versionTag} {version} · {content.footer}
                </div>
                {/* Bottom CTA to confirm reading without forcing precise scroll detection — saves the user
                    when scrollHeight measurement is off by a few pixels (common on mobile) */}
                {!readOnly && !hasReached && (
                  <div className="pt-3 flex justify-center">
                    <Button
                      type="button"
                      onClick={() => dispatch({ type: 'MARK_READ' })}
                      className="bg-amber-500/15 border border-amber-500/40 text-amber-200 hover:bg-amber-500/25 rounded-sm text-xs"
                      data-testid="nda-confirm-read-btn"
                    >
                      {lang === 'fr' ? '✓ J\'ai lu le contrat' : '✓ I have read the contract'}
                    </Button>
                  </div>
                )}
              </div>
              {!hasReached && !readOnly && (
                <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-[11px] bg-zinc-950/90 border border-zinc-800 px-2.5 py-1.5 rounded-sm text-zinc-400 pointer-events-none" data-testid="nda-scroll-hint">
                  <AlertTriangle className="w-3 h-3 text-amber-400" /> {T.scrollHint}
                </div>
              )}
            </>
          )}
        </div>

        {/* Signature block */}
        {!readOnly && (
        <div className="mt-3 bg-zinc-950 border border-zinc-800 rounded-sm p-4 space-y-3">
          {hasReached && (
            <div className="inline-flex items-center gap-1.5 text-[11px] text-emerald-400" data-testid="nda-read-ok">
              <CheckCircle2 className="w-3.5 h-3.5" /> {T.okScroll}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">{T.company}</Label>
              <Input value={companyName} disabled className="bg-zinc-900 border-zinc-800 text-zinc-400 rounded-sm h-9 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-zinc-400 text-xs">{T.fullName} *</Label>
              <Input
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder={T.namePlaceholder}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm h-9 text-sm"
                data-testid="nda-signed-name"
              />
            </div>
          </div>
          <label className={`flex items-start gap-2 text-xs ${hasReached ? 'text-zinc-300' : 'text-zinc-600 cursor-not-allowed'}`}>
            <input
              type="checkbox"
              checked={agreed}
              disabled={!hasReached}
              onChange={(e) => dispatch({ type: 'SET_AGREED', value: e.target.checked })}
              className="mt-0.5 accent-amber-500"
              data-testid="nda-agree-checkbox"
            />
            <span>{T.agreeLabel}</span>
          </label>
          <p className="text-[10px] text-zinc-600 leading-relaxed">{T.legalNote}</p>
        </div>
        )}

        <DialogFooter className="mt-3">
          {readOnly ? (
            <Button
              onClick={onClose}
              className="bg-zinc-100 text-zinc-950 hover:bg-white rounded-sm font-medium"
              data-testid="nda-readonly-close-btn"
            >
              {lang === 'fr' ? 'Fermer' : 'Close'}
            </Button>
          ) : (<>
            {!blocking && (
              <Button
                variant="outline"
                onClick={onClose}
                disabled={signing}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm"
                data-testid="nda-cancel-btn"
              >
                {T.cancel}
              </Button>
            )}
            <Button
              onClick={handleSign}
              disabled={!hasReached || !agreed || !signedName.trim() || signing}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium"
              data-testid="nda-sign-btn"
            >
              {signing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {T.sign}
            </Button>
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
